import {
    useState, ReactElement, ReactNode,
    JSXElementConstructor, memo,
    useEffect,
    MutableRefObject,
} from 'react';
import { Grid, Typography, GridSize, ClickAwayListener, CSSObject } from '@mui/material';
import { IComponentCss } from '@tap/mui-ui';
import EditIcon from '@mui/icons-material/Edit';

/**
 *  Поле с возможностью редактирования
 *  Может работать в двух режимах: просмотр и редактирование.
 */

type IFieldWrap = {
    children?: ReactNode,
    /** Заголовок поля */
    title?: ReactNode | ReactElement<any, any> | string,

    /** Значение поля в режиме просмотра */
    view?: string | number | ReactNode | ReactElement<any, any>,

    /** Значение поля в режиме редактирования. Если не задано, то компонент только в режиме просмотра */
    edit?: ReactElement<any, string | JSXElementConstructor<any>>,

    /** Количество колонок для большого разрешения (по умолчанию 6) */
    md?: boolean | GridSize | undefined,

    /** Количество колонок для маленького разрешения (по умолчанию 12) */
    xs?: boolean | GridSize | undefined,

    /**
     * Определяет как расположены заголовок: значение. Горизонтально или вертикально 
     * по умолчанию: горизонтально
     */
    direction?: 'horizontally' | 'vertically',

    /** Дополнительные стили для блока заголовка */
    titleWrapStyle?: CSSObject,

    /** Дополнительные стили для блока значения в режиме просмотра */
    viewWrapStyle?: CSSObject,

    /** Дополнительные стили для значения в режиме просмотра */
    viewTypographyStyle?: CSSObject,

    fieldRef?: MutableRefObject<FieldWithInlineEditApi | undefined>,

    onBlur?: () => void,
}


const css: IComponentCss = {
    blockWrap: {
        marginBottom: '6px',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
    },
    blockWrapH: {
    },
    blockWrapV: {
        display: 'block',
        marginBottom: '9px',
    },
    titleWrapH: {
        width: '150px',
        minWidth: '150px',
        alignItems: 'flex-start',
    },
    titleWrapV: {
        width: 'auto',
        paddingRight: '0px',
        display: 'block',
    },
    titleTypography: {
        color: '#999999',
    },
    viewWrapH: {
        flex: '1 auto',
        overflow: 'hidden',
        alignItems: 'flex-start',
        cursor: 'pointer',
    },
    viewWrapV: {
        display: 'block',
    },
    viewTypography: {
        border: '2px solid',
        borderColor: 'rgba(221, 221, 221, 0)',
        position: 'relative',
        transition: 'all .3s ease',
        // overflow: 'hidden',
        // whiteSpace: 'nowrap',
        // textOverflow: 'ellipsis',
    },
    viewTypographyH: {
        paddingLeft: '5px',
    },
    viewTypographyV: {
        paddingLeft: '0px',
    },
    viewTypographyEditMode: {
        borderRadius: '4px',
        '& div.wrap_edit_icon': {
            opacity: 0,
            transition: 'all .2s ease',
        },
        ':hover': {
            borderColor: 'rgba(221, 221, 221, 1)',
            cursor: 'text',
            '& div.wrap_edit_icon': {
                opacity: 1,
            },
        },
    },
    wrapEditIcon: {
        borderRadius: '0px 2px 2px 0px',
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: '#ddd',
        cursor: 'pointer',
    },
    editIcon: {
        fontSize: '20px',
    },
};

export type FieldWithInlineEditApi = {
    blur: () => void,
}

export const FieldWithInlineEdit = memo(({
    children,
    title,
    view,
    edit,
    md = 6,
    xs = 12,
    direction = 'horizontally',
    titleWrapStyle = {},
    viewWrapStyle = {},
    fieldRef,
    viewTypographyStyle = {},
    onBlur,
}: IFieldWrap): ReactElement => {
    const [isFocused, setIsFocused] = useState<boolean>(false);

    useEffect(() => {
        if (!fieldRef)
            return;

        fieldRef.current = {
            blur: () => setIsFocused(false),
        }
    }, [fieldRef])

    return (
        <Grid
            container
            item
            md={md}
            xs={xs}
            // justifyContent='flex-start'
            alignItems='end'
            sx={{
                ...css.blockWrap,
                ...(direction === 'horizontally' ? css.blockWrapH : css.blockWrapV),
            }}
        >
            {/* Заголовок */}
            {title &&
                <Grid
                    item
                    sx={{
                        ...(direction === 'horizontally' ? css.titleWrapH : css.titleWrapV),
                        ...titleWrapStyle,
                    }}
                    alignItems='stretch'
                >
                    <Typography sx={css.titleTypography}>
                        {title}:
                    </Typography>
                </Grid>
            }

            {/* Значение */}
            {!isFocused || !edit
                ? (
                    // Просмотр
                    <Grid
                        item
                        onClick={() => setIsFocused(true)}
                        sx={{
                            ...(direction === 'horizontally' ? css.viewWrapH : css.viewWrapV),
                            ...viewWrapStyle,
                        }}
                    >
                        <Typography
                            component='div'
                            sx={{
                                ...css.viewTypography,
                                ...(direction === 'horizontally' ? css.viewTypographyH : css.viewTypographyV),
                                ...(!!edit && css.viewTypographyEditMode),
                                ...viewTypographyStyle,
                            }}
                        >
                            {view}
                            {children}
                            {edit &&
                                <Grid className='wrap_edit_icon' sx={css.wrapEditIcon} >
                                    <EditIcon sx={css.editIcon} />
                                </Grid>
                            }
                        </Typography>
                    </Grid>
                )
                : (
                    // Редактирование
                    <ClickAwayListener
                        onClickAway={() => {
                            onBlur && onBlur()
                            setIsFocused(false)
                        }}
                        mouseEvent={'onMouseUp'}
                        touchEvent={'onTouchEnd'}>
                        <div style={{ width: '100%' }}>
                            {edit}
                        </div>
                    </ClickAwayListener>
                )
            }
        </Grid >
    );
});